import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import { Result } from '@Redux/type';
import { RequestAction, RequestFailure } from '@Repository/RequestAction';
import { MagicPath, Nullable } from '@Models';
import {
  MagicPathRepository,
  RequestMagicPathSuccess,
} from '@Repository/ninthAge';
import { ICrudEntityAction } from '@Redux/ICrudEntityAction';
import magicPathSlice from '@Redux/reducers/ninthage/magic-path.slice';
import type { PayloadAction } from '@reduxjs/toolkit';

@injectable()
export class MagicPathAction extends ICrudEntityAction<MagicPath> {
  @inject(TYPES.Repository.MagicPathRepository)
  private _repository!: MagicPathRepository;

  public fetchData =
    (armyId: number): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const cachedItems = await this.getCachedItems(armyId);
      if (cachedItems != null) dispatch(this.cacheDataSuccess(cachedItems));

      const response = await this.getItems(armyId);

      if (response instanceof RequestFailure) {
        const failure = response as RequestFailure;

        dispatch(this.haveError(failure.error.message));
        dispatch(this.areLoading(false));
        return;
      }

      const success = response as RequestMagicPathSuccess;
      dispatch(this.fetchDataSuccess(success.payload));
      dispatch(this.areLoading(false));
    };

  protected fetchDataSuccess(item: MagicPath): PayloadAction<MagicPath> {
    return magicPathSlice.actions.setMagicPath(item);
  }

  protected cacheDataSuccess(item: MagicPath): PayloadAction<MagicPath> {
    return magicPathSlice.actions.setMagicPath(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return magicPathSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return magicPathSlice.actions.setLoading(bool);
  }

  protected getCachedItems(id: number): Promise<Nullable<MagicPath>> {
    return this._repository.getCached(id, { locale: null });
  }

  protected getItems(id: number): Promise<RequestAction> {
    return this._repository.get(id, { locale: null });
  }
}
